import {
  type DragEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Capacitor } from '@capacitor/core';
import { useLocalization } from '@/hooks/useLocalization';

interface PersonalStorageDropZoneProps {
  enabled: boolean;
  onFilesDrop: (files: File[]) => void;
  children: ReactNode;
}

const carriesFiles = (event: DragEvent) =>
  Array.from(event.dataTransfer.types).includes('Files');

const PersonalStorageDropZone = ({
  enabled,
  onFilesDrop,
  children,
}: PersonalStorageDropZoneProps) => {
  const { t } = useLocalization();
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);
  const isWeb = Capacitor.getPlatform() === 'web';
  const acceptsDrop = enabled && isWeb;

  // Turning the zone off mid-drag skips the leave and drop handlers, so the
  // drag state is cleared here instead.
  useEffect(() => {
    if (!acceptsDrop) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  }, [acceptsDrop]);

  const handleDragEnter = (event: DragEvent) => {
    if (!acceptsDrop || !carriesFiles(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };

  // On the web a file drop is always canceled, even with the zone off, so the
  // browser does not open the file and leave the app.
  const handleDragOver = (event: DragEvent) => {
    if (!isWeb || !carriesFiles(event)) return;
    event.preventDefault();
    if (!acceptsDrop) {
      event.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDragLeave = (event: DragEvent) => {
    if (!acceptsDrop || !carriesFiles(event)) return;
    dragDepth.current = Math.max(dragDepth.current - 1, 0);
    if (dragDepth.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event: DragEvent) => {
    if (!isWeb || !carriesFiles(event)) return;
    event.preventDefault();
    if (!acceptsDrop) return;
    dragDepth.current = 0;
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files);
    event.dataTransfer.clearData();
    if (files.length > 0) {
      onFilesDrop(files);
    }
  };

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      {children}
      {isDragging && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            border: `2px dashed ${theme.palette.primary.main}`,
            borderRadius: 1,
            backgroundColor: theme.palette.action.selected,
            pointerEvents: 'none',
          })}
        >
          <UploadFileIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography
            variant='body1'
            color='primary'
          >
            {t('documentVault.dropFilesHere')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PersonalStorageDropZone;
